import { subDays } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Suspense, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information-circle.svg';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';

import { Metrics } from './metrics';
import { cn } from '@/utils/classnames';

const ANALYTICS_EXPANDED_KEY = 'source.dashboard.analyticsExpanded';

const defaultRange = {
  from: subDays(new Date(), 15),
  to: new Date(),
};

export function Analytics() {
  const intl = useIntl();

  // Keep track of the analytics expanded state in localStorage
  const [isExpanded, setIsExpanded] = useLocalStorageState(
    ANALYTICS_EXPANDED_KEY,
    true,
  );
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);

  return (
    <ErrorBoundary fallback={null}>
      <div className="shadow-base flex w-full flex-col overflow-x-auto rounded-lg bg-white">
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
          className={cn(
            'hover:bg-fx-neutral-50 flex flex-row items-center gap-4 border-b border-neutral-200 px-4 py-1.5',
            isExpanded ? 'rounded-t-lg' : 'rounded-lg',
          )}
        >
          {/* Title */}
          <div className="flex items-center gap-1">
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="Portfolio metrics"
                description="Heading for a summary of recruitment performance data"
              />
            </Typography>
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Spend, hires, and efficiency across all openings for the selected period.',
                description:
                  'Explanatory note: the portfolio metrics covers spending, hires, and efficiency across job openings over a selected period',
              })}
            >
              <img src={InfoIcon} alt="Info" className="h-4 w-4" />
            </Tooltip>
          </div>

          {/* Date filter */}
          <div onClick={e => e.stopPropagation()}>
            <DateRangePicker
              selectedDate={dateRange}
              setSelectedDate={setDateRange}
              defaultRange={defaultRange}
            />
          </div>

          {/* Expand/collapse */}
          <span className="ml-auto">
            {isExpanded ? (
              <ChevronUp className="text-fx-primary size-4" />
            ) : (
              <ChevronDown className="text-fx-primary size-4" />
            )}
          </span>
        </button>

        {isExpanded && (
          <Suspense fallback={<Metrics.Skeleton />}>
            <Metrics dateRange={dateRange} />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
}
